/**
 * Image optimization utilities for better performance
 */

const SUPABASE_PROJECT_URL = 'https://dvawogvuaomipakwlvst.supabase.co';

export type ImageSize = 'thumbnail' | 'medium' | 'full';

interface ImageSizeConfig {
  width: number;
  height: number;
  quality: number;
}

const IMAGE_SIZES: Record<ImageSize, ImageSizeConfig> = {
  thumbnail: { width: 400, height: 300, quality: 80 },
  medium: { width: 800, height: 600, quality: 85 },
  full: { width: 1920, height: 1440, quality: 90 },
};

/**
 * Generate optimized image URL
 * Supabase Storage public endpoints don't support transformation params,
 * so we return the original URL as-is to avoid ORB blocking issues.
 */
export const getOptimizedImageUrl = (url: string, _size: ImageSize = 'medium'): string => {
  return url || '';
};

/**
 * Generate srcset for responsive images
 */
/**
 * Generate srcset for responsive images
 * Disabled for Supabase Storage (no transformation support).
 */
export const generateSrcSet = (_url: string): string => {
  return '';
};

/**
 * Get sizes attribute for responsive images
 */
export const getImageSizes = (variant: 'card' | 'hero' | 'thumbnail' = 'card'): string => {
  switch (variant) {
    case 'hero':
      return '100vw';
    case 'thumbnail':
      return '(max-width: 640px) 100px, 150px';
    case 'card':
    default:
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px';
  }
};

/**
 * Generate a tiny placeholder URL for blur-up effect
 */
/**
 * Generate a placeholder URL for blur-up effect
 * Returns original URL since Supabase Storage doesn't support transforms.
 */
export const getPlaceholderUrl = (url: string): string => {
  return url || '';
};

/**
 * Preload images for better UX
 */
export const preloadImages = (urls: string[]): void => {
  urls.forEach((url) => {
    if (url) {
      const img = new Image();
      img.src = url;
    }
  });
};

/**
 * Preload optimized images with link preload for critical images
 */
export const preloadOptimizedImages = (urls: string[], size: ImageSize = 'medium'): void => {
  const optimizedUrls = urls.map((url) => getOptimizedImageUrl(url, size));
  
  // Use link preload for first 3 images (above fold)
  optimizedUrls.slice(0, 3).forEach((url) => {
    if (url && typeof document !== 'undefined') {
      const existingLink = document.querySelector(`link[href="${url}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
    }
  });
  
  // Regular preload for remaining images
  preloadImages(optimizedUrls.slice(3));
};

/**
 * Check if an image URL is valid and accessible
 */
export const isValidImageUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
